import * as React from "react";
import { Link } from "react-router-dom";
import { useManufacturer } from "../contexts/WithManufacturer";
import withRoot from "../withRoot";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ManufacturerOverviewPage,
  Skeleton,
  useChat,
} from "randmarcomps";
import {
  Handshake,
  AlertTriangle,
  Bot,
  Sparkles,
  ArrowRight,
  Layers,
  PackagePlus,
  ReceiptText,
  CheckCircle2,
  Circle,
} from "lucide-react";
import {
  useGetV4ManufacturerByRouteManufacturerIdCategoriesQuery,
  useGetV4ManufacturerByRouteManufacturerIdDocumentsReceiptsQuery,
  useGetV4PartnerByApplicationIdAccountManufacturerQuery,
  type Manufacturer,
} from "randmar-api-client";
import { useUser } from "../hooks/useUser";
import Path from "../routes/Path";

type PortalSignals = {
  categoriesCount: number;
  productsCount: number;
  activeOrdersCount: number;
  hasSalesVolume: boolean;
  numberOfOpportunities: number;
  numberOfResellers: number;
  isPortalEmpty: boolean;
};

type StepDefinition = {
  id: string;
  label: string;
  title: string;
  description: string;
  href: string;
  actionLabel: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

const buildOnboardingSteps = (applicationId: string): StepDefinition[] => [
  {
    id: "categories",
    label: "Assortments",
    title: "Create your first category",
    description: "Bundle flagship SKUs into a merch story so resellers know where to start.",
    href: Path.categories(applicationId),
    actionLabel: "Build assortment",
    Icon: Layers,
  },
  {
    id: "products",
    label: "Catalog",
    title: "Add detailed product data",
    description: "Upload specs, media, and inventory availability in minutes.",
    href: Path.insertProduct(applicationId),
    actionLabel: "Insert product",
    Icon: PackagePlus,
  },
  {
    id: "purchase-order",
    label: "Purchase Order",
    title: "Place your first PO",
    description: "Place high moving inventory in the Randmar warehouses.",
    href: Path.requisitions(applicationId),
    actionLabel: "Draft a PO",
    Icon: ReceiptText,
  },
  {
    id: "open-to-work",
    label: "Open to Work",
    title: "Chat with resellers",
    description: "Let resellers know you are ready to create business relationships.",
    href: Path.chat(applicationId),
    actionLabel: "Change status",
    Icon: Handshake,
  },
];

const assistantPrompts = [
  {
    id: "new-product",
    label: "New Product",
    description: "Launch a SKU with specs, pricing, and media.",
    prompt: "Please guide me to create a new product",
  },
  {
    id: "edit-product",
    label: "Edit Product",
    description: "Update inventory, copy, or imagery.",
    prompt: "Please guide me to edit a product",
  },
  {
    id: "create-opportunity",
    label: "Create Opportunity",
    description: "Spin up a fresh reseller opportunity.",
    prompt: "Please guide me to create an opportunity",
  },
  {
    id: "manage-qualification",
    label: "Manage Qualification",
    description: "Clarify who can access your catalog.",
    prompt: "Please guide me to manage the qualification of a reseller",
  },
] as const;

interface AssistantButtonsProps {
  className?: string;
}

function AssistantButtons({ className = "" }: AssistantButtonsProps) {
  const { sendMessageWithClear } = useChat();
  const headingId = React.useId();

  return (
    <section aria-labelledby={headingId} className={className}>
      <Card className="rounded-3xl border border-slate-100/80 bg-white/90 p-6 shadow-sm ring-1 ring-black/5">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-indigo-500">Need a jump start?</p>
            <CardTitle id={headingId} className="text-2xl text-slate-900">
              Guided setup with Randmar Assistant
            </CardTitle>
            <CardDescription className="text-sm text-slate-600">
              Ask for a workflow and we’ll open the right screens with context.
            </CardDescription>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-4 py-2 text-sm font-medium text-indigo-700">
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            Context-aware prompts
          </div>
        </div>
        <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4" role="list">
          {assistantPrompts.map(prompt => (
            <Button
              key={prompt.id}
              type="button"
              role="listitem"
              aria-label={`Ask the assistant: ${prompt.label}`}
              onClick={() => sendMessageWithClear(prompt.prompt)}
              className="assistant-prompt-button group flex h-full flex-col items-start justify-between rounded-2xl px-4 py-3 text-left text-slate-900 transition-all duration-200 ease-out focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60"
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Bot className="h-4 w-4 text-indigo-600 transition-transform duration-200 group-hover:scale-110" aria-hidden="true" />
                {prompt.label}
              </div>
              <p className="mt-1 text-sm text-slate-500">{prompt.description}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-indigo-600">
                Open assistant
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
              </span>
            </Button>
          ))}
        </div>
      </Card>
    </section>
  );
}

interface EmptyPortalNextStepsProps {
  applicationId: string;
  manufacturer?: Manufacturer;
  completedStepIds?: ReadonlySet<string>;
}

const EmptyPortalNextSteps: React.FC<EmptyPortalNextStepsProps> = ({
  applicationId,
  manufacturer,
  completedStepIds = new Set<string>(),
}) => {
  const { sendMessageWithClear } = useChat();

  const steps = React.useMemo<StepDefinition[]>(() => buildOnboardingSteps(applicationId), [applicationId]);

  return (
    <section aria-labelledby="next-steps-heading" className="mb-6">
      <Card className="overflow-hidden rounded-3xl border border-white/20 bg-gradient-to-r from-indigo-600 via-purple-600 to-rose-500 text-white shadow-xl ring-1 ring-black/10">
        <CardHeader className="border-b border-white/15 bg-white/5 px-6 py-8 lg:px-8">
          <div className="flex flex-col gap-4">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-white/90">
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                Getting started
              </div>
              <CardTitle id="next-steps-heading" className="mt-3 text-3xl text-white">
                {manufacturer?.PublicName ?? manufacturer?.Name ?? "Your brand"} portal is ready for its first drop
              </CardTitle>
              <CardDescription className="mt-2 text-base text-white/80">
                No live data yet? Follow these curated next steps to populate categories, catalog details, and operations
                workflows so resellers experience a complete story on day one.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-8 px-6 py-8 lg:px-8">
          <ol className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" role="list">
            {steps.map((step, index) => {
              const StepIcon = step.Icon;
              const completed = completedStepIds.has(step.id);
              return (
                <li key={step.id} className="h-full">
                  <Link
                    to={step.href}
                    aria-label={`${step.title} – ${step.actionLabel}`}
                    className="group relative flex h-full flex-col rounded-2xl border border-white/20 bg-white/10 p-5 text-white shadow-lg shadow-black/5 backdrop-blur transition-all duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-indigo-700 hover:border-white/40 motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-2xl"
                  >
                    {completed && (
                      <span className="absolute right-4 top-4 inline-flex items-center gap-1 text-xs font-semibold text-emerald-200">
                        <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                        <span className="sr-only">Step completed</span>
                      </span>
                    )}
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-white/80">
                      Step {index + 1}
                      <span className="text-white/40">•</span>
                      {step.label}
                    </div>
                    <div className="mt-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 text-white">
                      <StepIcon className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <p className="mt-4 text-lg font-semibold text-white">{step.title}</p>
                    <p className="mt-2 text-sm text-white/80">{step.description}</p>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-white">
                      {step.actionLabel}
                      <ArrowRight
                        className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                        aria-hidden="true"
                      />
                    </span>
                  </Link>
                </li>
              );
            })}
          </ol>
          <div className="rounded-2xl border border-dashed border-white/40 bg-white/10 p-5 backdrop-blur">
            <div className="flex flex-col gap-3 text-sm text-white/80 md:flex-row md:items-center md:justify-between">
              <p className="leading-relaxed">Pro tip: Invite users to the platform to perform different tasks.</p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="inline-flex items-center justify-center gap-1 rounded-full border-white/60 bg-white/10 px-4 text-white hover:bg-white/20"
                onClick={() => sendMessageWithClear("Invite User")}
              >
                Invite user
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
};

interface GettingStartedAccordionProps {
  applicationId: string;
  manufacturer?: Manufacturer;
  completedStepIds: ReadonlySet<string>;
}

const GettingStartedAccordion: React.FC<GettingStartedAccordionProps> = ({
  applicationId,
  manufacturer,
  completedStepIds,
}) => {
  const { sendMessageWithClear } = useChat();
  const steps = React.useMemo<StepDefinition[]>(() => buildOnboardingSteps(applicationId), [applicationId]);
  const defaultAccordionValue = React.useMemo(() => {
    const firstIncomplete = steps.find(step => !completedStepIds.has(step.id));
    return firstIncomplete?.id ?? steps[0]?.id;
  }, [steps, completedStepIds]);

  return (
    <section aria-labelledby="next-steps-accordion-heading" className="mb-6">
      <Card className="overflow-hidden rounded-3xl border border-white/20 bg-gradient-to-r from-indigo-600 via-purple-600 to-rose-500 text-white shadow-xl ring-1 ring-black/10">
        <CardHeader className="border-b border-white/15 bg-white/5 px-6 py-8 lg:px-8">
          <div className="flex flex-col gap-4">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-white/90">
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                Getting started
              </div>
              <CardTitle id="next-steps-accordion-heading" className="mt-3 text-3xl text-white">
                {manufacturer?.PublicName ?? manufacturer?.Name ?? "Your brand"} is almost marketplace-ready
              </CardTitle>
              <CardDescription className="mt-2 text-base text-white/80">
                Categories and products are live—now finish the operational workflows so Randmar can receive and move your
                inventory.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-6 py-8 lg:px-8">
          <Accordion
            type="single"
            collapsible
            defaultValue={defaultAccordionValue}
            className="space-y-4 text-white"
          >
            {steps.map((step, index) => {
              const StepIcon = step.Icon;
              const completed = completedStepIds.has(step.id);
              return (
                <AccordionItem
                  key={step.id}
                  value={step.id}
                  className="overflow-hidden rounded-2xl border border-white/10 bg-white/5 text-white backdrop-blur"
                >
                  <AccordionTrigger className="flex w-full items-center gap-4 px-4 py-3 text-left text-white hover:no-underline">
                    <div className="flex flex-1 items-center gap-4">
                      <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-white">
                        <StepIcon className="h-5 w-5" aria-hidden="true" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">
                          Step {index + 1}
                          <span className="px-2 text-white/40">•</span>
                          {step.label}
                        </p>
                        <p className="text-base font-semibold text-white">{step.title}</p>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-2 text-sm font-semibold">
                      {completed ? (
                        <>
                          <CheckCircle2 className="h-5 w-5 text-emerald-300" aria-hidden="true" />
                          Completed
                        </>
                      ) : (
                        <>
                          <Circle className="h-5 w-5 text-white/50" aria-hidden="true" />
                          Pending
                        </>
                      )}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 text-white/90">
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <p className="text-sm leading-relaxed">{step.description}</p>
                      <Link
                        to={step.href}
                        aria-label={`${step.title} – ${step.actionLabel}`}
                        className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-white hover:text-white/80"
                      >
                        {completed ? "Review" : step.actionLabel}
                        <ArrowRight className="h-4 w-4" aria-hidden="true" />
                      </Link>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
          <div className="mt-6 rounded-2xl border border-dashed border-white/40 bg-white/10 p-5 backdrop-blur">
            <div className="flex flex-col gap-3 text-sm text-white/80 md:flex-row md:items-center md:justify-between">
              <p className="leading-relaxed">Pro tip: Invite users to the platform to perform different tasks.</p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="inline-flex items-center justify-center gap-1 rounded-full border-white/60 bg-white/10 px-4 text-white hover:bg-white/20"
                onClick={() => sendMessageWithClear("Invite User")}
              >
                Invite user
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
};

const computePortalSignals = (manufacturer?: Manufacturer | null): PortalSignals => {
  const categoriesCount = manufacturer?.Categories?.length ?? 0;
  const productsCount =
    manufacturer?.Categories?.reduce((total, category) => total + (category?.Products?.length ?? 0), 0) ?? 0;
  const activeOrdersCount = manufacturer?.ActiveOrderDetails?.length ?? 0;
  const numberOfOpportunities = manufacturer?.NumberOfOpportunities ?? 0;
  const numberOfResellers = manufacturer?.NumberOfResellers ?? 0;

  const salesData = manufacturer?.SalesData;
  const rawSalesValues = [
    salesData?.SalesLast30Days ?? 0,
    salesData?.SalesLast3Months ?? 0,
    salesData?.SalesYearToDay ?? 0,
  ];
  const hasSalesVolume = rawSalesValues.some(value => (value ?? 0) > 0) || (manufacturer?.SalesStatistics?.length ?? 0) > 0;

  const isPortalEmpty =
    categoriesCount === 0 &&
    activeOrdersCount === 0 &&
    !hasSalesVolume &&
    numberOfOpportunities === 0 &&
    numberOfResellers === 0;

  return {
    categoriesCount,
    productsCount,
    activeOrdersCount,
    hasSalesVolume,
    numberOfOpportunities,
    numberOfResellers,
    isPortalEmpty,
  };
};

const Home: React.FC = () => {
  const { applicationId } = useManufacturer();
  const { user } = useUser();
  const userEmail = user?.profile?.email ?? "";

  const {
    data: manufacturer,
    isLoading,
    isError,
  } = useGetV4PartnerByApplicationIdAccountManufacturerQuery({
    applicationId: applicationId,
    withSpecification: true,
  });
  const {
    data: receipts,
    isLoading: isReceiptsLoading,
    isError: isReceiptsError,
  } = useGetV4ManufacturerByRouteManufacturerIdDocumentsReceiptsQuery(
    { routeManufacturerId: applicationId },
    { skip: !applicationId }
  );
  const { data: categories } = useGetV4ManufacturerByRouteManufacturerIdCategoriesQuery(
    { routeManufacturerId: applicationId },
    { skip: !applicationId }
  );

  const portalSignals = React.useMemo(() => computePortalSignals(manufacturer), [manufacturer]);
  const hasProductInCategory = React.useMemo(() => {
    if (!categories) {
      return portalSignals.productsCount > 0;
    }
    return categories.some(category => (category?.Products?.length ?? 0) > 0);
  }, [categories, portalSignals.productsCount]);
  const receiptsCount = receipts?.length ?? 0;
  const canEvaluatePurchaseHistory = !isReceiptsLoading && !isReceiptsError;
  const showGettingStarted = Boolean(
    manufacturer &&
      (portalSignals.isPortalEmpty || (canEvaluatePurchaseHistory && receiptsCount === 0))
  );
  const completedStepIds = React.useMemo(() => {
    const completed = new Set<string>();
    if (portalSignals.categoriesCount > 0) {
      completed.add("categories");
    }
    if (hasProductInCategory) {
      completed.add("products");
    }
    if (portalSignals.activeOrdersCount > 0 || receiptsCount > 0) {
      completed.add("purchase-order");
    }
    if (manufacturer?.OpenToWork || portalSignals.numberOfResellers > 0) {
      completed.add("open-to-work");
    }
    return completed;
  }, [portalSignals, receiptsCount, manufacturer?.OpenToWork, hasProductInCategory]);
  const showGettingStartedAccordion =
    Boolean(manufacturer) &&
    !portalSignals.isPortalEmpty &&
    completedStepIds.has("categories") &&
    completedStepIds.has("products") &&
    canEvaluatePurchaseHistory &&
    !completedStepIds.has("purchase-order");

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-1/3 mb-4" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
        <Skeleton className="h-64" />
      </div>
    )
  }

  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>
          There was a problem loading manufacturer information. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      {manufacturer?.OpenToWork && (
        <Alert variant="success" className="mb-4">
          <Handshake className="h-4 w-4" />
          <AlertTitle>Open to Work with Resellers</AlertTitle>
          <AlertDescription>
            <p>Resellers will be introducing their business, and communicating opportunities for your Brand through Randmar.</p>
            <p>You&rsquo;ll engage directly with resellers, allow them to learn about your product lines, and grow your business faster.</p>
          </AlertDescription>
        </Alert>
      )}
      {showGettingStarted && (
        <EmptyPortalNextSteps
          applicationId={applicationId}
          manufacturer={manufacturer}
          completedStepIds={completedStepIds}
        />
      )}
      {showGettingStartedAccordion && (
        <GettingStartedAccordion
          applicationId={applicationId}
          manufacturer={manufacturer}
          completedStepIds={completedStepIds}
        />
      )}
      <AssistantButtons className={"mb-6"} />
      {!showGettingStarted &&
        <ManufacturerOverviewPage
          applicationId={applicationId}
          readonly={true}
          withReports={true}
          userEmail={userEmail}
          manufacturer={manufacturer}
        />
      }
    </>
  );
}

export default withRoot(Home);
